import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  ArrowLeft, 
  Clock, 
  User, 
  Calendar,
  ArrowRight,
  Tag
} from 'lucide-react';

// Blog posts data
const blogPosts = [
  {
    slug: 'industry-trained-ai-knowledge',
    title: 'How Our Industry-Trained AI Knows Your Business Better Than Generic Chatbots',
    excerpt: 'Discover how our AI is pre-loaded with plumbing codes, HVAC troubleshooting guides, electrical regulations, and regional best practices — giving your customers expert-level answers instantly.',
    category: 'Technology',
    author: 'Alaska AI Services',
    date: 'February 25, 2026',
    readTime: '6 min read',
    image: null,
    featured: true
  },
  {
    slug: 'why-contractors-lose-leads',
    title: '5 Reasons Alaska Contractors Lose $2,000+ Weekly to Missed Calls',
    excerpt: 'Discover why 60% of inbound calls go unanswered and how AI automation can capture every lead, even during peak winter hours.',
    category: 'Lead Generation',
    author: 'Alaska AI Services',
    date: 'February 15, 2026',
    readTime: '5 min read',
    image: null,
    featured: false
  },
  {
    slug: 'ai-vs-answering-service',
    title: 'AI Receptionist vs Traditional Answering Service: Which is Better?',
    excerpt: 'Compare the costs, capabilities, and customer experience of AI automation versus hiring a traditional answering service.',
    category: 'Comparison',
    author: 'Alaska AI Services',
    date: 'February 10, 2026',
    readTime: '7 min read',
    image: null,
    featured: false
  },
  {
    slug: 'winter-emergency-response',
    title: 'How to Handle Winter Emergency Calls When You\'re Already on a Job',
    excerpt: 'Alaska winters mean furnace emergencies. Learn how to capture every emergency lead without leaving your current job site.',
    category: 'Operations',
    author: 'Alaska AI Services',
    date: 'February 5, 2026',
    readTime: '4 min read',
    image: null,
    featured: false
  },
  {
    slug: 'appointment-booking-automation',
    title: 'The Complete Guide to Automated Appointment Booking for Contractors',
    excerpt: 'Stop playing phone tag. Learn how automated booking systems can fill your calendar while you focus on the work.',
    category: 'Automation',
    author: 'Alaska AI Services',
    date: 'January 28, 2026',
    readTime: '6 min read',
    image: null,
    featured: false
  },
  {
    slug: 'customer-response-time',
    title: 'Why Response Time is the #1 Factor in Winning Contractor Jobs',
    excerpt: 'Studies show customers call an average of 3 contractors. The first to respond wins 78% of the time. Here\'s how to always be first.',
    category: 'Sales',
    author: 'Alaska AI Services',
    date: 'January 20, 2026',
    readTime: '5 min read',
    image: null,
    featured: false
  },
  {
    slug: 'roi-ai-automation',
    title: 'Calculating ROI: Is AI Lead Response Worth It for Your Business?',
    excerpt: 'A detailed breakdown of costs, time savings, and revenue impact when implementing AI automation for your contracting business.',
    category: 'Business',
    author: 'Alaska AI Services',
    date: 'January 15, 2026',
    readTime: '8 min read',
    image: null,
    featured: false
  }
];

const categoryColors = {
  'Lead Generation': 'bg-green-500/20 text-green-400',
  'Comparison': 'bg-purple-500/20 text-purple-400',
  'Operations': 'bg-orange-500/20 text-orange-400',
  'Automation': 'bg-sky-500/20 text-sky-400',
  'Sales': 'bg-yellow-500/20 text-yellow-400',
  'Business': 'bg-blue-500/20 text-blue-400',
  'Technology': 'bg-emerald-500/20 text-emerald-400'
};

export default function BlogPage() {
  const featuredPost = blogPosts.find(p => p.featured);
  const regularPosts = blogPosts.filter(p => !p.featured);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg">Alaska AI Services</span>
              </div>
            </div>
            <Link 
              to="/"
              className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            The Alaska AI Services Blog
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Tips, strategies, and insights to help Alaska contractors capture more leads and grow their business.
          </p>
        </motion.div>

        {/* Featured Post */}
        {featuredPost && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <Link to={`/blog/${featuredPost.slug}`}>
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-2xl p-8 sm:p-10 hover:border-sky-500/50 transition-all group">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-3 py-1 rounded-full bg-sky-500/20 text-sky-400 text-xs font-medium">
                    Featured
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[featuredPost.category]}`}>
                    {featuredPost.category}
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 group-hover:text-sky-400 transition-colors">
                  {featuredPost.title}
                </h2>
                <p className="text-lg text-slate-400 mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-6 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {featuredPost.date}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {featuredPost.readTime}
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        )}

        {/* Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regularPosts.map((post, index) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <Link to={`/blog/${post.slug}`}>
                <article className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-all h-full flex flex-col group">
                  <div className="mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[post.category]}`}>
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 group-hover:text-sky-400 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-slate-400 mb-4 flex-1 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </div>
                  </div>
                </article>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-br from-sky-500/10 to-blue-600/10 border border-sky-500/20 rounded-2xl p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to Stop Losing Leads?
            </h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              See how Alaska AI Services can capture every lead and book appointments automatically.
            </p>
            <a 
              href="https://calendly.com/jondcurtis"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-all"
            >
              Book a Free Demo
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 Alaska AI Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Export blog posts for use in other components
export { blogPosts };
